import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { GripVertical } from 'lucide-react';

const ItemTypes = {
    EMPLOYEE_ROW: 'EMPLOYEE_ROW'
};

const DraggableEmployeeRow = ({
                                  employee,
                                  index,
                                  moveEmployee,
                                  children,
                                  isSelected,
                                  onSelectItem
                              }) => {
    const ref = useRef(null);

    // Create the drag item object
    const dragItem = React.useMemo(() => ({
        type: ItemTypes.EMPLOYEE_ROW,
        index,
        id: employee.id,
        employee
    }), [index, employee]);

    // useDrag hook
    const [{ isDragging }, drag] = useDrag({
        type: ItemTypes.EMPLOYEE_ROW,
        item: dragItem,
        canDrag: (monitor) => {
            // Ne pas autoriser le drag si l'utilisateur clique sur la colonne Actions
            const target = monitor.getInitialClientOffset();
            if (!target) return true;

            // Vérifier si le clic initial est sur un élément avec data-no-drag
            const element = document.elementFromPoint(target.x, target.y);
            if (element) {
                const noDragElement = element.closest('[data-no-drag]');
                if (noDragElement) return false;
            }

            return true;
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    // useDrop hook
    const [{ isOver }, drop] = useDrop({
        accept: ItemTypes.EMPLOYEE_ROW,
        hover: (item, monitor) => {
            if (!ref.current || !item) return;

            const dragIndex = item.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) return;

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();

            if (!clientOffset) return;

            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

            moveEmployee(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    // Combine drag and drop refs
    drag(drop(ref));

    return (
        <tr
            ref={ref}
            className={`
                transition-all duration-200
                ${isDragging ? 'opacity-50 bg-gray-100 dark:bg-gray-700' : ''}
                ${isOver ? 'bg-primary/10' : ''}
                ${isSelected ? 'bg-primary/5 dark:bg-primary/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}
                border-b border-gray-200 dark:border-gray-700
                relative
            `}
            style={{
                position: 'relative'
            }}
        >
            {/* Drag Handle Column - Hidden on mobile */}
            <td className="w-10 pl-6 pr-0 py-4">
                <div
                    className="cursor-move text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title="Glisser pour réorganiser"
                >
                    <GripVertical size={20} />
                </div>
            </td>

            {/* Checkbox Column */}
            <td className="px-6 py-4 w-12">
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => onSelectItem(employee.id, e)}
                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
            </td>

            {/* Children (other columns) */}
            {children}
        </tr>
    );
};

export default DraggableEmployeeRow;
